// ************************************************************
// SECCIÓN: IMPORTACIONES Y CONFIGURACIÓN DEL COMPONENTE
// ************************************************************
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import '../styles/ClientManagement.css';

const ClientManagement = () => {
  // Estado para almacenar los clientes
  const [clients, setClients] = useState([]);
  // Estado para la búsqueda de clientes
  const [searchQuery, setSearchQuery] = useState('');
  // Estado para controlar la visibilidad del modal y cliente en edición (si aplica)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  // Estado del formulario para clientes
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });

  // ************************************************************
  // SECCIÓN: CICLO DE VIDA - OBTENER CLIENTES
  // ************************************************************
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/api/clients');
      setClients(res.data);
    } catch (err) {
      console.error('Error al obtener clientes:', err);
    }
  };

  // ************************************************************
  // SECCIÓN: MÉTODOS PARA ABRIR/CERRAR MODAL Y MANIPULAR FORMULARIO
  // ************************************************************
  const handleOpenModal = (client = null) => {
    if (client) {
      // Modo edición: llenamos el formulario con los datos del cliente
      setEditingClient(client);
      setFormData({ ...client });
    } else {
      // Modo creación
      setEditingClient(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingClient(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ************************************************************
  // SECCIÓN: MÉTODO DE ENVÍO DEL FORMULARIO (CREAR O EDITAR)
  // ************************************************************
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Actualizar cliente existente
        await api.put(`/api/clients/${editingClient.id}`, formData);
      } else {
        // Crear nuevo cliente
        await api.post('/api/clients', formData);
      }
      fetchClients();
      handleCloseModal();
    } catch (err) {
      console.error('Error al guardar cliente:', err);
    }
  };

  // ************************************************************
  // SECCIÓN: MÉTODO PARA BORRAR UN CLIENTE
  // ************************************************************
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este cliente?')) {
      try {
        await api.delete(`/api/clients/${id}`);
        fetchClients();
      } catch (err) {
        console.error('Error al eliminar cliente:', err);
      }
    }
  };

  // ************************************************************
  // SECCIÓN: FILTRADO DE CLIENTES POR BÚSQUEDA
  // ************************************************************
  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ************************************************************
  // SECCIÓN: RENDERIZADO DEL COMPONENTE
  // ************************************************************
  return (
    <div className="client-management">
      {/* HEADER */}
      <header className="cm-header">
        <h1>Gestión de Clientes</h1>
        <div className="cm-search-bar">
          <input
            type="text"
            className="cm-search-input"
            placeholder="Buscar Cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="cm-search-button">Buscar</button>
        </div>
        <button className="add-button" onClick={() => handleOpenModal()}>
          Añadir Cliente
        </button>
      </header>

      {/* GRID DE CLIENTES */}
      <div className="cm-grid">
        {filteredClients.map((client) => (
          <div key={client.id} className="cm-card">
            <div className="cm-card-body">
              <h3>{client.name}</h3>
              {client.email && <p>Email: {client.email}</p>}
              {client.phone && <p>Tel: {client.phone}</p>}
              {client.address && <p>Dir.: {client.address}</p>}
              {client.notes && <p>Notas: {client.notes}</p>}
              <div className="cm-card-actions">
                <button onClick={() => handleOpenModal(client)}>Editar</button>
                <button onClick={() => handleDelete(client.id)}>Eliminar</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="cm-modal-overlay">
          <div className="cm-modal">
            <h2>{editingClient ? 'Editar Cliente' : 'Añadir Cliente'}</h2>
            <form onSubmit={handleSubmit} className="cm-form">
              <label>
                Nombre:
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Email:
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </label>

              <label>
                Teléfono:
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </label>

              <label>
                Dirección:
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </label>

              <label>
                Notas:
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                />
              </label>

              <div className="cm-form-actions">
                <button type="submit">
                  {editingClient ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={handleCloseModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
